// Slice 8 — Channels Hub (rebuilt).
// Per-channel configuration with provider-specific public + secret fields,
// validate, simulate-inbound, send-test, and webchat embed snippet.
import { useEffect, useState, useMemo } from "react";
import {
  Loader2, Save, Settings as SettingsIcon, MessageSquare, Phone, AtSign,
  Globe, Instagram, Facebook, Send, Mic, FlaskConical, Copy, Code2,
  CheckCircle2, XCircle, ArrowDownToLine,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const ICONS = {
  voice: Phone, whatsapp: MessageSquare, sms: MessageSquare, email: AtSign,
  webchat: Globe, instagram: Instagram, facebook: Facebook, telegram: Send,
  viber: MessageSquare, voicemail: Mic,
};

const TONE = {
  ok:   "bg-emerald-100 text-emerald-900 border-emerald-300",
  fail: "bg-red-100 text-red-900 border-red-300",
  none: "bg-slate-100 text-slate-700 border-slate-300",
};

function fmtFieldKey(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

export default function ChannelsHub() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // channel string
  const { tenant, refresh } = useTenant();
  const [omniBusy, setOmniBusy] = useState(false);
  const omniEnabled = tenant?.settings?.omnichannel_enabled !== false;
  const [copilotBusy, setCopilotBusy] = useState(false);
  const copilotEnabled = tenant?.settings?.ai_copilot_enabled !== false;
  const [rtStatus, setRtStatus] = useState(null);   // {registered, webhook}
  const [rtBusy, setRtBusy] = useState(false);
  const hasPostproxy = rows.some((r) => r.provider === "postproxy" && r.enabled);

  async function load() {
    setLoading(true);
    try { const r = await api.get("/channels"); setRows(r.data || []); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/channels/postproxy/webhook-status").then((r) => setRtStatus(r.data)).catch(() => {});
  }, []);

  async function enableRealtime() {
    if (!tenant?.id) return;
    setRtBusy(true);
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
      const callback_url = `${base}/api/webhooks/postproxy/${tenant.id}`;
      await api.post("/channels/postproxy/register-webhook", { callback_url });
      const r = await api.get("/channels/postproxy/webhook-status");
      setRtStatus(r.data);
      toast.success("Real-time delivery enabled — DMs now arrive instantly");
    } catch (e) { toast.error(formatErr(e)); } finally { setRtBusy(false); }
  }

  async function toggleOmni(enabled) {
    if (!tenant?.id) return;
    setOmniBusy(true);
    try {
      await api.patch(`/tenants/${tenant.id}`, {
        settings: { ...(tenant.settings || {}), omnichannel_enabled: enabled },
      });
      await refresh?.();
      toast.success(enabled ? "Omnichannel section enabled for agents" : "Agents now run voice-only (Omnichannel hidden)");
    } catch (e) { toast.error(formatErr(e)); } finally { setOmniBusy(false); }
  }

  async function toggleCopilot(enabled) {
    if (!tenant?.id) return;
    setCopilotBusy(true);
    try {
      await api.patch(`/tenants/${tenant.id}`, {
        settings: { ...(tenant.settings || {}), ai_copilot_enabled: enabled },
      });
      await refresh?.();
      toast.success(enabled ? "AI Copilot enabled for agents" : "AI Copilot hidden from the Agent Workspace");
    } catch (e) { toast.error(formatErr(e)); } finally { setCopilotBusy(false); }
  }

  async function quickToggle(channel, enabled) {
    try {
      await api.put(`/channels/${channel}`, { enabled });
      load();
      toast.success(enabled ? `Channel '${channel}' enabled` : `Channel '${channel}' disabled`);
    } catch (e) { toast.error(formatErr(e)); }
  }

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="channels-hub-title">
            Omnichannel Hub
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Configure providers for each channel. Live delivery is simulator-backed —
            once you save credentials we'll validate them and surface inbound + send-test
            so you can prove the loop.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase shrink-0">
          Simulator-mode
        </Badge>
      </div>

      {/* Tenant-wide Omnichannel section toggle for the Agent Workspace */}
      <Card className="p-4 flex items-center justify-between gap-4" data-testid="omni-section-toggle-card">
        <div className="min-w-0">
          <div className="font-medium flex items-center gap-2">
            Omnichannel section in Agent Workspace
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
            When ON, agents see the omni inbox + conversation panel with channel switching.
            When OFF, the workspace runs <strong>voice-only</strong> — the CRM lead screen-pop
            still works for every call type.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground">{omniEnabled ? "Enabled" : "Voice-only"}</span>
          <Switch
            data-testid="omni-section-toggle"
            checked={omniEnabled}
            disabled={omniBusy || !tenant?.id}
            onCheckedChange={toggleOmni}
          />
        </div>
      </Card>

      {/* Tenant-wide AI Copilot toggle for the Agent Workspace */}
      <Card className="p-4 flex items-center justify-between gap-4" data-testid="copilot-section-toggle-card">
        <div className="min-w-0">
          <div className="font-medium flex items-center gap-2">
            AI Copilot in Agent Workspace
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
            When ON, agents see the AI Copilot panel (live suggestions, compose, summarise).
            This is independent of the Omnichannel section — you can run <strong>voice-only +
            Copilot</strong>, <strong>omni + Copilot</strong>, or either alone. The CRM lead
            screen-pop always works.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground">{copilotEnabled ? "Enabled" : "Hidden"}</span>
          <Switch
            data-testid="copilot-section-toggle"
            checked={copilotEnabled}
            disabled={copilotBusy || !tenant?.id}
            onCheckedChange={toggleCopilot}
          />
        </div>
      </Card>

      {/* Real-time (PostProxy webhook) delivery for DM channels */}
      {hasPostproxy && (
        <Card className="p-4 flex items-center justify-between gap-4" data-testid="realtime-card">
          <div className="min-w-0">
            <div className="font-medium">Real-time DM delivery (Instagram / Facebook / Telegram)</div>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
              Register a PostProxy webhook so inbound messages arrive in the agent inbox
              <strong> instantly</strong> instead of on the periodic sync.
              {rtStatus?.registered && rtStatus?.webhook?.callback_url
                ? ` Active → ${rtStatus.webhook.callback_url}`
                : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {rtStatus?.registered
              ? <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300" data-testid="realtime-active">Active</Badge>
              : <Badge variant="outline">Polling (15s)</Badge>}
            <Button
              data-testid="realtime-enable-btn"
              size="sm" variant={rtStatus?.registered ? "outline" : "default"}
              onClick={enableRealtime} disabled={rtBusy}
            >
              {rtBusy ? "Registering…" : rtStatus?.registered ? "Re-sync webhook" : "Enable real-time"}
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map((r) => {
            const Icon = ICONS[r.channel] || MessageSquare;
            const testTone = r.last_test_status === "ok" ? TONE.ok
                            : r.last_test_status === "fail" ? TONE.fail : TONE.none;
            return (
              <Card key={r.channel} className="p-4 space-y-3" data-testid={`channel-card-${r.channel}`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-md bg-muted grid place-items-center"><Icon className="w-4 h-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium flex items-center gap-1.5">
                      {r.label}
                      {r.builtin && <Badge variant="outline" className="h-4 text-[9px] uppercase">Built-in</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {r.provider ? `Provider: ${r.provider}` : "No provider selected"}
                    </div>
                  </div>
                  <Switch
                    data-testid={`channel-toggle-${r.channel}`}
                    checked={r.enabled}
                    onCheckedChange={(v) => quickToggle(r.channel, v)}
                  />
                </div>

                <div className="flex items-center gap-1.5 text-[11px]">
                  {r.last_test_status ? (
                    <span className={`px-1.5 py-0.5 rounded border ${testTone}`} title={r.last_test_detail}>
                      {r.last_test_status === "ok" ? <CheckCircle2 className="w-3 h-3 inline mr-0.5"/> : <XCircle className="w-3 h-3 inline mr-0.5"/>}
                      {r.last_test_status === "ok" ? "Validated" : "Issues"}
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200">Not tested</span>
                  )}
                  <span className="text-muted-foreground">·</span>
                  <span className="text-muted-foreground">{r.secret_keys?.length || 0} secrets · {Object.keys(r.public || {}).length} fields</span>
                </div>

                <Button
                  data-testid={`channel-configure-${r.channel}`}
                  onClick={() => setEditing(r.channel)}
                  variant="outline" size="sm" className="w-full gap-1.5"
                >
                  <SettingsIcon className="w-3.5 h-3.5" /> Configure
                </Button>
              </Card>
            );
          })}
        </div>
      )}

      {editing && (
        <ChannelDialog
          channel={editing}
          row={rows.find((r) => r.channel === editing)}
          onClose={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------------------
//  Channel detail dialog
// -------------------------------------------------------------
function ChannelDialog({ channel, row, onClose }) {
  const [data, setData] = useState(row);
  const [savingProv, setSavingProv] = useState(false);
  const [pubDraft, setPubDraft] = useState({});
  const [secretDraft, setSecretDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data && data.provider) {
      setPubDraft({ ...(data.public || {}) });
      setSecretDraft({});
    }
  }, [data?.provider, data?.channel]);

  const provider = useMemo(
    () => (data?.providers || []).find((p) => p.id === data?.provider),
    [data?.provider, data?.providers]
  );

  async function refreshRow() {
    try { const r = await api.get(`/channels/${channel}`); setData(r.data); }
    catch (e) { toast.error(formatErr(e)); }
  }

  async function pickProvider(provId) {
    setSavingProv(true);
    try {
      const r = await api.put(`/channels/${channel}`, { provider: provId });
      setData(r.data);
    } catch (e) { toast.error(formatErr(e)); } finally { setSavingProv(false); }
  }

  async function saveConfig() {
    setBusy(true);
    try {
      const body = { public: pubDraft };
      const cleanSecrets = {};
      Object.entries(secretDraft).forEach(([k, v]) => { if (v !== "") cleanSecrets[k] = v; });
      if (Object.keys(cleanSecrets).length) body.secrets = cleanSecrets;
      const r = await api.put(`/channels/${channel}`, body);
      setData(r.data);
      setSecretDraft({});
      toast.success("Configuration saved");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }

  async function runTest() {
    setBusy(true);
    try {
      const r = await api.post(`/channels/${channel}/test`);
      toast[r.data.status === "ok" ? "success" : "error"](r.data.detail);
      await refreshRow();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid={`channel-dialog-${channel}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Configure {data?.label || channel}
            {data?.builtin && <Badge variant="outline" className="h-5 text-[10px] uppercase">Built-in</Badge>}
          </DialogTitle>
          <DialogDescription>
            All secrets are encrypted with Fernet before storage.
            {channel === "whatsapp"
              ? " Validate makes a live authenticated call to WATI to verify your endpoint + token."
              : " Simulator-mode validates the config without contacting the real provider."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Provider picker */}
          <div className="space-y-2">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Provider</Label>
            <Select value={data?.provider || ""} onValueChange={pickProvider} disabled={savingProv}>
              <SelectTrigger data-testid={`channel-provider-${channel}`} className="w-full"><SelectValue placeholder="Choose a provider…"/></SelectTrigger>
              <SelectContent>
                {(data?.providers || []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {provider && (
            <>
              {/* Public fields */}
              {provider.public.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Public configuration</Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {provider.public.map((k) => (
                      <div key={k} className="space-y-1">
                        <Label className="text-xs">{fmtFieldKey(k)}</Label>
                        <Input
                          data-testid={`pub-${channel}-${k}`}
                          value={pubDraft[k] ?? ""}
                          onChange={(e) => setPubDraft({ ...pubDraft, [k]: e.target.value })}
                          placeholder={k}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Secret fields */}
              {provider.secrets.length > 0 && (
                <div className="space-y-3">
                  <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Secrets (Fernet-encrypted)</Label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {provider.secrets.map((k) => {
                      const masked = data?.secrets_masked?.[k];
                      return (
                        <div key={k} className="space-y-1">
                          <Label className="text-xs flex items-center justify-between">
                            <span>{fmtFieldKey(k)}</span>
                            {masked && <span className="text-[10px] font-mono text-muted-foreground">{masked}</span>}
                          </Label>
                          <Input
                            data-testid={`sec-${channel}-${k}`}
                            type="password"
                            value={secretDraft[k] ?? ""}
                            placeholder={masked ? "(leave empty to keep)" : "Paste secret…"}
                            onChange={(e) => setSecretDraft({ ...secretDraft, [k]: e.target.value })}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Save + Test row */}
              <div className="flex items-center gap-2 pt-1">
                <Button data-testid={`save-${channel}`} onClick={saveConfig} disabled={busy} className="gap-1.5">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save className="w-3.5 h-3.5"/>} Save
                </Button>
                <Button data-testid={`test-${channel}`} onClick={runTest} variant="outline" disabled={busy} className="gap-1.5">
                  <FlaskConical className="w-3.5 h-3.5"/> Validate
                </Button>
                {data?.last_test_status === "ok" && (
                  <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300">Last validated ok</Badge>
                )}
              </div>

              {channel === "webchat" && data?.provider === "builtin" && <WebchatSnippet />}

              {channel === "whatsapp" && data?.provider === "wati" && <WatiWebhookPanel />}

              {channel === "viber" && data?.provider === "viber_bot" && <ViberWebhookPanel />}

              {channel !== "voice" && data?.enabled && (
                <SimulateInboundForm channel={channel} />
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WatiWebhookPanel() {
  const { tenant } = useTenant();
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const url = tenant?.id ? `${base}/api/webhooks/wati/${tenant.id}` : "";

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Webhook URL copied");
    } catch { toast.error("Copy failed — select & copy manually"); }
  }

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3" data-testid="wati-webhook-card">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5"/> Inbound webhook — paste in WATI dashboard
        </Label>
        <Button size="sm" variant="ghost" onClick={copy} className="h-7 gap-1" disabled={!url}>
          <Copy className="w-3.5 h-3.5"/> Copy
        </Button>
      </div>
      <Input data-testid="wati-webhook-url" readOnly value={url} className="font-mono text-[11px]"/>
      <p className="text-[10px] text-muted-foreground italic">
        In WATI → Settings → Webhooks, add this URL and enable the "Message Received"
        event so incoming WhatsApp messages arrive in the agent inbox instantly.
        The <b>API endpoint</b> is your WATI server URL (WATI → API Docs), e.g.
        <span className="font-mono"> https://live-mt-server.wati.io/xxxxx</span>.
      </p>
    </div>
  );
}

function ViberWebhookPanel() {
  const { tenant } = useTenant();
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const url = tenant?.id ? `${base}/api/webhooks/viber/${tenant.id}` : "";
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/channels/viber/webhook-status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  async function register() {
    if (!url) return;
    setBusy(true);
    try {
      await api.post("/channels/viber/register-webhook", { callback_url: url });
      const r = await api.get("/channels/viber/webhook-status");
      setStatus(r.data);
      toast.success("Viber webhook registered — messages now arrive instantly");
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3" data-testid="viber-webhook-card">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5"/> Inbound webhook (auto-registered with Viber)
        </Label>
        {status?.registered
          ? <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300" data-testid="viber-webhook-active">Active</Badge>
          : <Badge variant="outline">Not registered</Badge>}
      </div>
      <Input data-testid="viber-webhook-url" readOnly value={url} className="font-mono text-[11px]"/>
      <div className="flex items-center gap-2">
        <Button data-testid="viber-register-webhook" size="sm" onClick={register} disabled={busy || !url} className="gap-1.5">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
          {status?.registered ? "Re-register webhook" : "Register with Viber"}
        </Button>
        {status?.account_name && (
          <span className="text-[11px] text-muted-foreground">Account: {status.account_name}</span>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground italic">
        Save your <b>auth token</b> above (Viber Admin Panel → your Bot → <b>Info → Token</b>),
        click <b>Validate</b>, then <b>Register with Viber</b>. Viber pushes every inbound
        message to this URL so it lands in the agent inbox instantly.
      </p>
    </div>
  );
}

function WebchatSnippet() {
  const [snippet, setSnippet] = useState(null);
  const [loading, setLoading] = useState(false);

  async function fetchSnippet() {
    setLoading(true);
    try { const r = await api.get("/channels/webchat/embed-snippet"); setSnippet(r.data); }
    catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }
  useEffect(() => { fetchSnippet(); }, []);

  async function copyToClipboard() {
    if (!snippet?.snippet) return;
    try {
      await navigator.clipboard.writeText(snippet.snippet);
      toast.success("Snippet copied to clipboard");
    } catch { toast.error("Copy failed — select & copy manually"); }
  }

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3" data-testid="webchat-snippet-card">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1.5">
          <Code2 className="w-3.5 h-3.5"/> Embed snippet for your website
        </Label>
        <Button size="sm" variant="ghost" onClick={copyToClipboard} className="h-7 gap-1" disabled={!snippet}>
          <Copy className="w-3.5 h-3.5"/> Copy
        </Button>
      </div>
      {loading ? (
        <div className="py-3 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto"/></div>
      ) : (
        <Textarea
          data-testid="webchat-snippet-code"
          readOnly value={snippet?.snippet || ""}
          rows={8} className="font-mono text-[11px] resize-none"
        />
      )}
      <p className="text-[10px] text-muted-foreground italic">
        {snippet?.note || ""}
      </p>
    </div>
  );
}

function SimulateInboundForm({ channel }) {
  const [from_name, setFromName]     = useState("Test Visitor");
  const [from_handle, setFromHandle] = useState(
    channel === "email" ? "test@example.com" : "+919876543210"
  );
  const [body, setBody]              = useState("Hi! Just testing the inbox via the simulator.");
  const [busy, setBusy]              = useState(false);

  async function fire() {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/channels/${channel}/simulate-inbound`, {
        from_name, from_handle, body,
      });
      toast.success(`Inbound dropped on conversation ${r.data.conversation_id.slice(0, 8)}…`);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50/40 p-3" data-testid={`sim-inbound-card-${channel}`}>
      <Label className="text-xs flex items-center gap-1.5 text-amber-900">
        <ArrowDownToLine className="w-3.5 h-3.5"/> Simulate inbound message (agents will see it live)
      </Label>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input data-testid={`sim-from-${channel}`} value={from_name} onChange={(e) => setFromName(e.target.value)} placeholder="From name"/>
        <Input data-testid={`sim-handle-${channel}`} value={from_handle} onChange={(e) => setFromHandle(e.target.value)} placeholder={channel === "email" ? "from@email" : "+91…"}/>
      </div>
      <Textarea data-testid={`sim-body-${channel}`} value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Message body…"/>
      <Button data-testid={`sim-fire-${channel}`} onClick={fire} disabled={busy} size="sm" className="w-full">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1"/> : null} Push to inbox
      </Button>
    </div>
  );
}
